/* eslint-disable global-require */
import { ENABLE_DEV_MSW, IS_DEV, PRICEMOOV_VERSION } from 'env';
import { setAutoFreeze } from 'immer';
import { getMoonConfig, moonRetry } from 'moonConfig';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import 'setimmediate';

import { MoonProvider, MutationCache, QueryCache, QueryClient } from '@pricemoov-oss/moon';
import '@pricemoov-oss/react-table/dist/style/index.css';
import '@pricemoov/pollux-css/dist/base.css';

import 'helpers/i18n';
import App from 'redesign/components/app/App';
import PolluxProvider from 'redesign/components/common/PolluxProvider';
import AuthManager from 'redesign/components/common/contexts/AuthManager';
import logger from 'services/logger/logger';
import { hotjarSetup } from 'services/third-party-apps/hotjar';
import sentrySetup from 'services/third-party-apps/sentry';

import './redesign/css/style.css';
import reportWebVitals from './reportWebVitals';

// Third party app configuration
setAutoFreeze(false);
sentrySetup();
hotjarSetup();

const container = document.getElementById('root');
const root = createRoot(container);

// Overrides the default store behaviour defined in moon:
// https://github.com/PricemoovOSS/moon/blob/master/packages/moon/src/utils/client.ts#L77-L85
const moonStore = new QueryClient({
    queryCache: new QueryCache(),
    mutationCache: new MutationCache(),
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false, // coming from default behaviour
            retry: moonRetry,
        },
    },
});

root.render(
    <MoonProvider links={getMoonConfig()} store={moonStore}>
        <Router>
            <AuthManager>
                <PolluxProvider>
                    <App />
                </PolluxProvider>
            </AuthManager>
        </Router>
    </MoonProvider>
);

logger.info(`Pricemoov platform v${PRICEMOOV_VERSION} successfully started!`);

if (IS_DEV && ENABLE_DEV_MSW) {
    // MSW dev handlers are an opt-in playground (`src/redesign/mocks/dev/handlers.ts`
    // is empty by default). Starting the service worker when no handlers are
    // registered still routes every request through MSW pass-through, which
    // trips on same-origin navigations to SPA routes (`/home`, `/login`, ...)
    // with "Failed to convert value to 'Response'" because MSW can't stream
    // Vite's index.html fallback. Gate MSW behind `VITE_ENABLE_DEV_MSW=true`
    // in `.env.local` so the dev flow is clean unless you're actively mocking.
    import('./redesign/mocks/dev/browser').then(({ worker }) =>
        worker.start({
            onUnhandledRequest(req, print) {
                const url = new URL(req.url);
                // Skip Vite-served modules and SPA navigations — only real
                // cross-origin API calls should surface as "unhandled".
                if (url.origin === window.location.origin) {
                    return;
                }
                print.warning();
            },
        })
    );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
